import type NavItem from '../nav-item/NavItem.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { EventController } from '../common/controllers/EventController.js'
import { SlotController } from '../common/controllers/SlotController.js'
import componentStyle from '../common/styles/Component.css'
import style from './Navigation.css'

/**
 * Navigation is used to display the primary navigation in the sidebar
 * of an application. Navigation includes a list of links that users
 * use to move between sections of the application.
 *
 * @status ready
 * @category navigation
 * @slot - The main section of the sidebar, for holding nav components.
 * @slot header - The top section of the sidebar.
 * @slot footer - The bottom section of the sidebar.
 *
 * @fires {NordEvent} toggle - Dispatched whenever a nav item's state changes between open and closed.
 * @fires {NordEvent} activate - Dispatched whenever a nav item has been marked as active
 *
 * @cssprop [--n-navigation-background-color=var(--n-color-nav-surface)] - Controls the background color of the navigation element.
 */
@customElement('nord-navigation')
export default class Navigation extends LitElement {
  static styles = [componentStyle, style]

  private headerSlot = new SlotController(this, 'header')
  private events = new EventController(this)
  private footerObserver: MutationObserver | null = null
  private allowItemsToRemainOpen = false

  /**
   * Controls whether the navigations's footer has sticky positioning.
   */
  @property({ type: Boolean, reflect: true, attribute: 'sticky-footer' }) stickyFooter: boolean = false

  connectedCallback() {
    super.connectedCallback()
    // if we add this event listener during render
    // we seem to miss the first activate event.
    // so let's add it as early as possible.
    this.events.listen(this, 'activate', this.handleActivate)

    // in cases where there is an initially active nav-item,
    // we should allow any nav-items with the `open` attribute to remain open.
    const activeItem = this.querySelector('nord-nav-item[active]')
    this.allowItemsToRemainOpen = activeItem != null

    if (typeof window !== 'undefined') {
      this.observeFooter()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.footerObserver?.disconnect()
    this.footerObserver = null
  }

  /**
   * Observe the footer slot element for visibility changes.
   * Watches style and class attributes on the footer element itself, and
   * childList on the host to detect footer being added/removed (e.g. v-if).
   */
  private observeFooter() {
    this.footerObserver = new MutationObserver(() => this.requestUpdate())

    // Watch for footer element being added/removed from the host
    this.footerObserver.observe(this, { childList: true })

    // Watch hidden attribute on the footer element directly
    const footerEl = this.querySelector(':scope > [slot="footer"]')
    if (footerEl) {
      this.footerObserver.observe(footerEl, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }
  }

  private get isFooterVisible() {
    // In SSR, the element is backed by a minimal DOM shim that does not implement
    // querySelector or window.getComputedStyle. Return true so the footer slot
    // renders as visible during server-side rendering.
    if (typeof window === 'undefined') {
      return true
    }

    const footerEl = this.querySelector<HTMLElement>(':scope > [slot="footer"]')
    if (!footerEl) {
      return false
    }

    const style = window.getComputedStyle(footerEl)
    return (
      style.display !== 'none'
      && style.visibility !== 'hidden'
      && style.visibility !== 'collapse'
      && style.opacity !== '0'
      && style.contentVisibility !== 'hidden'
    )
  }

  render() {
    return html`
      <slot name="header" ?hidden=${this.headerSlot.isEmpty}></slot>
      <div class="n-navigation-main">
        <nav>
          <slot></slot>
        </nav>
        ${!this.stickyFooter ? html`<slot name="footer" ?hidden=${!this.isFooterVisible}></slot>` : nothing}
      </div>
      ${this.stickyFooter ? html`<slot name="footer" ?hidden=${!this.isFooterVisible}></slot>` : nothing}
    `
  }

  private handleActivate = (e: Event) => {
    const target = e.target as NavItem
    const ancestors = new Set<NavItem>()

    // walk up the DOM, gathering all ancestors from the target to its top-most nav-item.
    // it's more efficient to first gather ancestors here,
    // than to repeatedly call item.contains(target) in the for-loop below.
    let parent = target.parentElement?.closest('nord-nav-item')
    while (parent) {
      ancestors.add(parent)
      parent = parent.parentElement?.closest('nord-nav-item')
    }

    // open and activate any items on the path, otherwise close and deactivate.
    // however, if the item is set to open initially, we should not close it.
    for (const item of this.querySelectorAll('nord-nav-item')) {
      if (item !== target) {
        const isAncestor = ancestors.has(item)
        item.open = isAncestor || (this.allowItemsToRemainOpen && item.open)
        item.active = isAncestor
      }
    }

    // this is a one-time thing, purely for initial open state
    this.allowItemsToRemainOpen = false
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-navigation': Navigation
  }
}
